import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import StoryToday from "@/components/StoryToday";
import StoryDMs from "@/components/StoryDMs";
import NewPost from "@/components/NewPost";

const TYPES = {
  blog_it: { l: "Blog IT", c: "bg-olive text-canvas" },
  blog_en: { l: "Blog EN", c: "bg-teal-600 text-white" },
  instagram: { l: "Instagram", c: "bg-terracotta/90 text-canvas" },
  facebook: { l: "Facebook", c: "bg-sky-700 text-white" },
  facebook_instagram: { l: "FB + IG", c: "bg-amber-600 text-white" },
  reel: { l: "Reel", c: "bg-fuchsia-700 text-white" },
  story: { l: "Storia IG", c: "bg-rose-500 text-white" },
};
const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const isFailed = (s) => s && (s.startsWith("cancelled") || s === "failed");

function Legend() {
  return (
    <div className="flex flex-wrap gap-2" data-testid="editorial-legend">
      {Object.entries(TYPES).map(([k, t]) => <span key={k} className={`text-xs px-2 py-1 rounded-full ${t.c}`}>{t.l}</span>)}
      <span className="text-xs px-2 py-1 rounded-full border border-line text-stone line-through">annullato / fallito</span>
    </div>
  );
}

function Chip({ it, onClick }) {
  const t = TYPES[it.platform] || { l: it.platform, c: "bg-sand text-ink" };
  return (
    <button onClick={() => onClick(it)} data-testid={`editorial-item-${it.id}`}
      className={`w-full text-left text-[11px] leading-tight px-1.5 py-1 rounded-md truncate transition-transform hover:scale-[1.03] ${t.c} ${isFailed(it.status) ? "opacity-40 line-through" : ""}`}>
      {new Date(it.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {it.title}
    </button>
  );
}

function ItemDialog({ it, onClose }) {
  return (
    <Dialog open={!!it} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-canvas max-w-lg" data-testid="editorial-dialog">
        {it && <>
          <DialogHeader><DialogTitle className="font-serif text-xl">{it.title}</DialogTitle></DialogHeader>
          <div className="text-xs uppercase tracking-widest text-stone">{(TYPES[it.platform] || {}).l} · {it.status} · {new Date(it.date).toLocaleString("it-IT")}</div>
          {it.image && <img src={it.image} alt="" className="rounded-xl w-full h-48 object-cover" />}
          {it.preview && <p className="text-sm text-ink/80 whitespace-pre-line max-h-56 overflow-auto">{it.preview}</p>}
          {it.slug && <a href={`/blog/${it.slug}`} target="_blank" rel="noreferrer" className="text-sm underline text-olive" data-testid="editorial-open-article">Apri articolo →</a>}
        </>}
      </DialogContent>
    </Dialog>
  );
}

export default function EditorialCalendar() {
  const [cur, setCur] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [rk, setRk] = useState(0);
  useEffect(() => { api.get(`/content/calendar?month=${ym(cur)}`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [cur, rk]);

  const cells = useMemo(() => {
    const lead = (cur.getDay() + 6) % 7;
    const n = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => i + 1)];
  }, [cur]);
  const byDay = useMemo(() => {
    const m = {};
    items.forEach((it) => { const d = new Date(it.date); if (d.getMonth() === cur.getMonth()) (m[d.getDate()] ||= []).push(it); });
    return m;
  }, [items, cur]);
  const shift = (k) => setCur(new Date(cur.getFullYear(), cur.getMonth() + k, 1));
  const today = new Date();

  return (
    <div className="space-y-5" data-testid="editorial-calendar">
      <StoryToday />
      <StoryDMs />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="rounded-full" onClick={() => shift(-1)} data-testid="editorial-prev"><CaretLeft size={16} /></Button>
          <div className="font-serif text-2xl text-ink capitalize w-48 text-center" data-testid="editorial-month">{cur.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</div>
          <Button size="icon" variant="outline" className="rounded-full" onClick={() => shift(1)} data-testid="editorial-next"><CaretRight size={16} /></Button>
        </div>
        <div className="flex items-center gap-3"><span className="text-sm text-stone" data-testid="editorial-count">{items.length} contenuti nel mese</span><NewPost onSaved={() => setRk((k) => k + 1)} /></div>
      </div>
      <Legend />
      <div className="grid grid-cols-7 gap-1.5">
        {WD.map((w) => <div key={w} className="text-xs uppercase tracking-widest text-stone text-center py-1">{w}</div>)}
        {cells.map((d, i) => {
          const isToday = d && today.getDate() === d && today.getMonth() === cur.getMonth() && today.getFullYear() === cur.getFullYear();
          return (
            <div key={i} data-testid={d ? `editorial-day-${d}` : undefined}
              className={`min-h-[96px] rounded-xl p-1.5 space-y-1 ${d ? "border border-line bg-canvas" : ""} ${isToday ? "ring-2 ring-olive" : ""}`}>
              {d && <div className="text-xs text-stone">{d}</div>}
              {(byDay[d] || []).map((it) => <Chip key={it.id} it={it} onClick={setSel} />)}
            </div>
          );
        })}
      </div>
      <ItemDialog it={sel} onClose={() => setSel(null)} />
    </div>
  );
}
